import React, { useRef, useEffect } from "react";
import { ActionPrompt, ActionPromptRelation } from "./actionPromptTypes";
import { WanActionPromptSelect } from "./WanActionPromptSelect";
import { WanPromptGuidance } from "./WanPromptGuidance";
export { WanPromptGuidance, WanActionPromptSelect };

export interface OutfitTypeTag {
  id: string;
  code: string;
  name: string;
  enabled?: boolean;
  groupCode?: string;
  groupId?: string;
}

export interface OutfitTagGroup {
  id: string;
  code: string;
  name: string;
  enabled?: boolean;
}

export type LocalSupplementalItem = {
  key: string;
  kind: "existing" | "new";
  id?: string;
  imageUrl?: string;
  file?: File;
  previewUrl?: string;
};

const LABELS = ["图4", "图5", "图6", "图7"];

export function isAvailableOutfitTypeTag(
  tagID: string,
  outfitTags: OutfitTypeTag[],
  outfitTagGroups?: OutfitTagGroup[],
) {
  return outfitTags.some((tag) => {
    if (tag.id !== tagID || tag.enabled === false) return false;
    if (tag.groupCode === "outfit_type") return true;
    return outfitTagGroups?.some((group) => group.id === tag.groupId && group.code === "outfit_type") ?? false;
  });
}

export function getAvailableBodyCoverageTags(
  outfitTags: OutfitTypeTag[],
  outfitTagGroups?: OutfitTagGroup[],
): OutfitTypeTag[] {
  const coverageGroup = outfitTagGroups?.find((g) => g.code === "body_coverage");
  if (coverageGroup && coverageGroup.enabled === false) {
    return [];
  }
  const coverageGroupId = coverageGroup?.id;
  return outfitTags.filter((tag) => {
    if (tag.enabled === false) return false;
    const isCoverageGroup =
      (tag.groupCode === "body_coverage" && (!coverageGroup || coverageGroup.enabled !== false)) ||
      (coverageGroupId ? tag.groupId === coverageGroupId : false) ||
      (outfitTagGroups?.some((g) => g.id === tag.groupId && g.code === "body_coverage" && g.enabled !== false) ?? false);
    if (!isCoverageGroup) return false;
    return tag.code === "full_body" || tag.code === "half_body";
  });
}

export function isAvailableBodyCoverageTag(
  tagID: string,
  outfitTags: OutfitTypeTag[],
  outfitTagGroups?: OutfitTagGroup[],
): boolean {
  if (!tagID) return false;
  return getAvailableBodyCoverageTags(outfitTags, outfitTagGroups).some((tag) => tag.id === tagID);
}

export function getDefaultFullBodyTag(
  outfitTags: OutfitTypeTag[],
  outfitTagGroups?: OutfitTagGroup[],
): OutfitTypeTag | undefined {
  const available = getAvailableBodyCoverageTags(outfitTags, outfitTagGroups);
  return available.find((tag) => tag.code === "full_body");
}

export function getTemplateBodyCoverageTag(
  template: { outfitFilterTags?: OutfitTypeTag[] },
  outfitTags?: OutfitTypeTag[],
  outfitTagGroups?: OutfitTagGroup[],
): OutfitTypeTag | undefined {
  const coverageGroupId = outfitTagGroups?.find((g) => g.code === "body_coverage")?.id;
  const assigned = template.outfitFilterTags?.find((t) => {
    if (t.groupCode === "body_coverage") return true;
    if (coverageGroupId && t.groupId === coverageGroupId) return true;
    if (outfitTags) {
      const fullTag = outfitTags.find((tag) => tag.id === t.id);
      if (fullTag?.groupCode === "body_coverage") return true;
      if (coverageGroupId && fullTag?.groupId === coverageGroupId) return true;
    }
    return false;
  });
  if (assigned) return assigned;
  if (outfitTags) {
    return getDefaultFullBodyTag(outfitTags, outfitTagGroups);
  }
  return undefined;
}

interface WanTemplateGenerationFieldsProps {
  outfitTypeTagId: string;
  onOutfitTypeTagIdChange: (id: string) => void;
  bodyCoverageTagId: string;
  onBodyCoverageTagIdChange: (id: string) => void;
  templateFilterTags?: OutfitTypeTag[];
  outfitTags: OutfitTypeTag[];
  outfitTagGroups?: OutfitTagGroup[];
  supplementalItems: LocalSupplementalItem[];
  onSupplementalItemsChange: (items: LocalSupplementalItem[]) => void;
  actionPromptIds?: string[];
  onActionPromptIdsChange?: (ids: string[]) => void;
  allActionPrompts?: ActionPrompt[];
  templateActionPrompts?: ActionPromptRelation[];
  disabled?: boolean;
}

export function WanTemplateGenerationFields({
  outfitTypeTagId,
  onOutfitTypeTagIdChange,
  bodyCoverageTagId,
  onBodyCoverageTagIdChange,
  templateFilterTags,
  outfitTags,
  outfitTagGroups,
  supplementalItems,
  onSupplementalItemsChange,
  actionPromptIds = [],
  onActionPromptIdsChange,
  allActionPrompts = [],
  templateActionPrompts = [],
  disabled = false,
}: WanTemplateGenerationFieldsProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const createdUrlsRef = useRef<Set<string>>(new Set());

  const availableOutfitTypeTags = outfitTags.filter((tag) =>
    isAvailableOutfitTypeTag(tag.id, outfitTags, outfitTagGroups)
  );
  const selectedOutfitTypeTag = outfitTags.find((tag) => tag.id === outfitTypeTagId);
  const hasUnavailableOutfitTypeTag = !!outfitTypeTagId &&
    !availableOutfitTypeTags.some((tag) => tag.id === outfitTypeTagId);

  const availableBodyCoverageTags = getAvailableBodyCoverageTags(outfitTags, outfitTagGroups);
  const selectedBodyCoverageTag =
    outfitTags.find((tag) => tag.id === bodyCoverageTagId) ||
    templateFilterTags?.find((tag) => tag.id === bodyCoverageTagId);
  const hasUnavailableBodyCoverageTag = !!bodyCoverageTagId &&
    !availableBodyCoverageTags.some((tag) => tag.id === bodyCoverageTagId);
  // Clean up all generated object URLs on unmount
  useEffect(() => {
    const urls = createdUrlsRef.current;
    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
      urls.clear();
    };
  }, []);

  const handleFileAdd = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length === 0) return;

    const remainingSlots = 4 - supplementalItems.length;
    if (remainingSlots <= 0) return;

    const filesToAdd = selectedFiles.slice(0, remainingSlots);
    const newItems: LocalSupplementalItem[] = filesToAdd.map((file) => {
      const url = URL.createObjectURL(file);
      createdUrlsRef.current.add(url);
      return {
        key: `new-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
        kind: "new",
        file,
        previewUrl: url,
      };
    });

    onSupplementalItemsChange([...supplementalItems, ...newItems]);
    if (e.target) e.target.value = "";
  };

  const removeItem = (index: number) => {
    const item = supplementalItems[index];
    if (item && item.kind === "new" && item.previewUrl) {
      URL.revokeObjectURL(item.previewUrl);
      createdUrlsRef.current.delete(item.previewUrl);
    }
    const updated = supplementalItems.filter((_, i) => i !== index);
    onSupplementalItemsChange(updated);
  };

  const moveItem = (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= supplementalItems.length) return;
    const updated = [...supplementalItems];
    const [removed] = updated.splice(fromIndex, 1);
    updated.splice(toIndex, 0, removed);
    onSupplementalItemsChange(updated);
  };

  return (
    <div className="space-y-4 pt-2 border-t border-gray-100">
      {/* 1. 服装大类 & 服装范围选择 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* 1.1 服装大类选择 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            服装大类 <span className="text-red-500">*</span>
          </label>
          <select
            required
            disabled={disabled}
            value={outfitTypeTagId}
            onChange={(e) => onOutfitTypeTagIdChange(e.target.value)}
            className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 disabled:opacity-60"
          >
            <option value="">— 请选择服装大类 —</option>
            {hasUnavailableOutfitTypeTag && (
              <option value={outfitTypeTagId} disabled>
                {selectedOutfitTypeTag
                  ? `当前选择不可用：${selectedOutfitTypeTag.name} (${selectedOutfitTypeTag.code})`
                  : "当前选择不可用：该服装大类已不存在"}
              </option>
            )}
            {availableOutfitTypeTags.map((tag) => (
              <option key={tag.id} value={tag.id}>
                {tag.name} ({tag.code})
              </option>
            ))}
          </select>
          {hasUnavailableOutfitTypeTag ? (
            <p className="mt-1 text-xs text-red-600">
              当前关联的服装大类不可用，请重新选择后再保存。
            </p>
          ) : (
            <p className="mt-1 text-xs text-gray-400">
              万相模型要求指定关联的服装大类，生成时将结合用户性别、年龄段及季节属性从服装库抽取对应款式。
            </p>
          )}
        </div>

        {/* 1.2 服装范围选择 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            服装范围 <span className="text-red-500">*</span>
          </label>
          <select
            required
            disabled={disabled}
            value={bodyCoverageTagId}
            onChange={(e) => onBodyCoverageTagIdChange(e.target.value)}
            className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 disabled:opacity-60"
          >
            <option value="">— 请选择服装范围 —</option>
            {hasUnavailableBodyCoverageTag && (
              <option value={bodyCoverageTagId} disabled>
                {selectedBodyCoverageTag
                  ? `当前选择不可用：${selectedBodyCoverageTag.name} (${selectedBodyCoverageTag.code})`
                  : "当前选择不可用：该服装范围已不存在"}
              </option>
            )}
            {availableBodyCoverageTags.map((tag) => (
              <option key={tag.id} value={tag.id}>
                {tag.name} ({tag.code})
              </option>
            ))}
          </select>
          {hasUnavailableBodyCoverageTag ? (
            <p className="mt-1 text-xs text-red-600">
              当前关联的服装范围不可用，请重新选择后再保存。
            </p>
          ) : (
            <p className="mt-1 text-xs text-gray-400">
              万相模型要求指定服装范围（全身/半身），生成时将限定在对应覆盖范围的服装款式中进行匹配。
            </p>
          )}
        </div>
      </div>

      {/* 2. 补充参考图 (ordered max-4) */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="block text-sm font-medium text-gray-700">
            补充参考图 <span className="text-xs text-gray-400 font-normal">（最多 4 张，按顺序对应 图4~图7）</span>
          </label>
          <span className="text-xs font-medium text-gray-500">{supplementalItems.length} / 4</span>
        </div>

        {supplementalItems.length < 4 && (
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              disabled={disabled}
              className="hidden"
              onChange={handleFileAdd}
            />
            <button
              type="button"
              disabled={disabled}
              onClick={() => fileInputRef.current?.click()}
              className="w-full border-2 border-dashed border-gray-200 rounded-lg p-3 flex items-center justify-center gap-2 cursor-pointer hover:border-teal-300 hover:bg-teal-50/30 transition-colors text-gray-500 disabled:opacity-60"
            >
              <i className="ri-image-add-line text-lg text-teal-600" />
              <span className="text-xs font-medium text-gray-700">上传补充参考图（按当前顺序图4~图7追加）</span>
            </button>
          </div>
        )}

        {supplementalItems.length > 0 && (
          <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
            {supplementalItems.map((item, index) => {
              const label = LABELS[index] || `图${index + 4}`;
              const preview = item.kind === "existing" ? item.imageUrl : item.previewUrl;
              return (
                <div
                  key={item.key}
                  className="flex items-center justify-between p-2 bg-gray-50 border border-gray-200 rounded-lg text-xs"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="px-2 py-1 bg-teal-100 text-teal-800 font-semibold rounded text-[11px] shrink-0">
                      {label}
                    </span>
                    {preview ? (
                      <img
                        src={preview}
                        alt={label}
                        className="w-10 h-10 object-cover rounded border border-gray-200 shrink-0"
                      />
                    ) : (
                      <div className="w-10 h-10 bg-gray-200 rounded flex items-center justify-center shrink-0">
                        <i className="ri-image-line text-gray-400" />
                      </div>
                    )}
                    <span className="truncate text-gray-600">
                      {item.kind === "new" && item.file ? item.file.name : "已上传的参考图"}
                    </span>
                  </div>

                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      disabled={disabled || index === 0}
                      onClick={() => moveItem(index, index - 1)}
                      title="上移"
                      className="w-7 h-7 flex items-center justify-center text-gray-500 hover:text-teal-600 hover:bg-gray-200 rounded disabled:opacity-30"
                    >
                      <i className="ri-arrow-up-line text-sm" />
                    </button>
                    <button
                      type="button"
                      disabled={disabled || index === supplementalItems.length - 1}
                      onClick={() => moveItem(index, index + 1)}
                      title="下移"
                      className="w-7 h-7 flex items-center justify-center text-gray-500 hover:text-teal-600 hover:bg-gray-200 rounded disabled:opacity-30"
                    >
                      <i className="ri-arrow-down-line text-sm" />
                    </button>
                    <button
                      type="button"
                      disabled={disabled}
                      onClick={() => removeItem(index)}
                      title="移除"
                      className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                    >
                      <i className="ri-delete-bin-line text-sm" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Prompt notice */}
        {supplementalItems.length > 0 && (
          <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2 text-xs text-amber-800">
            <i className="ri-error-warning-line text-amber-600 shrink-0 mt-0.5" />
            <span>
              提示：提示词中若有显式「图4」、「图5」等序号引用，调整补充图顺序后请手动更新提示词中的序号，系统不会自动修改提示词内容。
            </span>
          </div>
        )}
      </div>

      {/* 3. 适用动作提示词白名单 (Optional Action Prompt Whitelist) */}
      {onActionPromptIdsChange && (
        <WanActionPromptSelect
          value={actionPromptIds}
          onChange={onActionPromptIdsChange}
          allPrompts={allActionPrompts}
          initialTemplatePrompts={templateActionPrompts}
          disabled={disabled}
        />
      )}

      {/* 3. 固定输入契约预览 (Fixed Input Contract) */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2">
        <div className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
          <i className="ri-stack-line text-teal-600" />
          万相模型输入序列契约预览
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
          <div className="bg-white border border-gray-200 rounded p-2 flex flex-col items-center text-center">
            <span className="font-bold text-teal-700 mb-0.5">图1</span>
            <span className="text-gray-800 font-medium">模板底图</span>
            <span className="text-[10px] text-gray-400 mt-0.5">固定场景/主体</span>
          </div>
          <div className="bg-white border border-gray-200 rounded p-2 flex flex-col items-center text-center">
            <span className="font-bold text-teal-700 mb-0.5">图2</span>
            <span className="text-gray-800 font-medium">运行时人像</span>
            <span className="text-[10px] text-gray-400 mt-0.5">用户上传自拍</span>
          </div>
          <div className="bg-white border border-gray-200 rounded p-2 flex flex-col items-center text-center">
            <span className="font-bold text-teal-700 mb-0.5">图3</span>
            <span className="text-gray-800 font-medium">匹配服装</span>
            <span className="text-[10px] text-gray-400 mt-0.5">大类+范围+属性选定</span>
          </div>
          <div className="bg-white border border-gray-200 rounded p-2 flex flex-col items-center text-center">
            <span className="font-bold text-teal-700 mb-0.5">图4+</span>
            <span className="text-gray-800 font-medium">补充参考图</span>
            <span className="text-[10px] text-gray-400 mt-0.5">
              {supplementalItems.length > 0 ? `当前 ${supplementalItems.length} 张` : "未配置"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
